import { prefetchLayout } from '@/utils/prefetchLayout';
import { getStepWithParams } from '@/utils/checkoutUtils';
import { LoggerService } from '@/services';
import { PAGES } from '@/constants/pages';
import { STEPS } from '@/constants/checkout';
import { LOG_MSG } from '@/constants/logger';
import { STORE_ID } from '@/constants/stores';

export interface CustomWindow extends Window {
  ApplePaySession: ApplePaySession;
}

// declare let window: CustomWindow;

/**
 * Prepare the getServerSideProps returned object.
 * @param {object} param an object containing root, cart and auth stores
 * @return {object} the object that must be returned by the caller (getServerSideProps)
 */
const getPaymentIds = ({ root, cart, auth, expro }) => {
  const storeId = root?.storeConf?.storeId || LOG_MSG.MISSING.STORE;
  const userId = auth?.loggedUser?.userId || LOG_MSG.MISSING.USER;
  const cartId = cart?.content?.id || LOG_MSG.MISSING.CART;
  const exproOrderId = expro?.exproOrder?._id || LOG_MSG.MISSING.EXPRO_ORDER;

  return { userId, storeId, cartId, exproOrderId };
};

const getPaymentIdsForExproOrder = ({ root, expro, auth }) => {
  const storeId = root?.storeConf?.storeId || LOG_MSG.MISSING.STORE;
  const userId = auth?.loggedUser?.userId || LOG_MSG.MISSING.USER;
  const exproOrderId = expro?.exproOrder?._id || LOG_MSG.MISSING.EXPRO_ORDER;

  return { userId, storeId, exproOrderId };
};

export const processPaymentStatus = async (ctx, store, logSource, callback, exproOrder = false) => {
  // Retrieve basic store/user info, no need to get any layout data
  await prefetchLayout(ctx, store, false);

  const { root, cart, auth, expro } = store.getState();
  const { userId, storeId, cartId, exproOrderId } = getPaymentIds({ root, cart, auth, expro });

  let logMessage;
  if (exproOrder) {
    logMessage = (msg, data) => LoggerService.sendLog(
      exproOrderId, msg, storeId, logSource, userId, data
    );
  } else {
    logMessage = (msg, data) => LoggerService.sendLog(
      cartId, msg, storeId, logSource, userId, data
    );
  }

  let step;
  let success;
  let secure;
  if (exproOrder) {
    success = () => (
      PAGES.EXPRO_ORDER_REPORT
    );

    secure = (status) => (
      `${PAGES.EXPRO_ORDER}?code=${status || '0'}&defaultMessage=3ds`
    );
  } else {
    step = (step, code, message) => (
      `${PAGES.CHECKOUT}/${getStepWithParams(step, code, message)}`
    );

    success = () => (
      step(STEPS.REPORT)
    );

    secure = (status) => (
      step(STEPS.PAYMODE, status || '0', '3ds')
    );
  }

  const successSaveCard = () => (
    `${PAGES.ACCOUNT}/cards`
  );

  let destination;

  try {
    if (exproOrder) {
      destination = await callback(
        expro.exproOrder, { userId, storeId, exproOrderId }, logMessage, { success }
      );
    } else {
      destination = await callback(
        cart, { userId, storeId, cartId }, logMessage, { step, success, secure, successSaveCard }
      );
    }
  } catch (error) {
    logMessage(LOG_MSG.REDIRECT.ERROR, error);
    destination = secure(error.status);
  }

  return {
    redirect: {
      destination,
      permanent: false
    }
  };
};

export const isApplePayEnabled = (storeId: string, window: CustomWindow): boolean => {
  let isApplePayEnabled = false;

  const userAgentString = window?.navigator?.userAgent;
  
  const isAndroid = /Android/i.test(userAgentString);
  const isIPhoneOrIPad = /iPad|iPhone/i.test(userAgentString);
  const iOSVersionMatch = userAgentString.match(/OS (\d+)_(\d+)/);
  const iOSVersion = iOSVersionMatch ? parseFloat(`${iOSVersionMatch[1]}.${iOSVersionMatch[2]}`) : 0;
  const isCorrectIosVersion = iOSVersion >= 11.2;

  const canMakePayments = window?.['ApplePaySession'] && ApplePaySession.canMakePayments && ApplePaySession.canMakePayments();

  const shouldTurnApplePayEnabledTrueOnChina = isIPhoneOrIPad && isCorrectIosVersion && canMakePayments;

  const loggerObject = {
    isIPhoneOrIPad, iOSVersionMatch, iOSVersion, isCorrectIosVersion, canMakePayments, shouldTurnApplePayEnabledTrueOnChina
  };

  LoggerService.sendLog(null, LOG_MSG.APPLEPAY.DATA, storeId, null, null, loggerObject);

  if ([STORE_ID.HONG_KONG, STORE_ID.SINGAPORE].includes(storeId)) {
    isApplePayEnabled = shouldTurnApplePayEnabledTrueOnChina;
  } else if (!isAndroid) {
    isApplePayEnabled = true;
  }

  return isApplePayEnabled;
};
